import React from 'react';
import { motion } from 'framer-motion';
import { Factory, CheckCircle, ArrowRight, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const IndustrialIndustryPage: React.FC = () => {
  const systems = [
    {
      title: 'Inline Pressure Filters',
      description: 'Continuous operation with automated brush or vacuum cleaning',
      link: '/filtration/inline/big'
    },
    {
      title: 'Compact TIG Series',
      description: 'Minimal footprint for skid or wall-mount integration',
      link: '/filtration/inline/tig'
    },
    {
      title: 'Water Box Assemblies',
      description: 'High-flow coarse filtration for washdown and process streams',
      link: '/filtration/water-intake'
    }
  ];

  const benefits = [
    'Stainless construction for long life and corrosion resistance',
    'Continuous operation with automated brush or vacuum cleaning',
    'Compact footprint for skid or wall-mount integration',
    'Compatible with food-grade, process, and washdown applications',
    'Low maintenance with self-cleaning technology',
    'Custom mesh sizes from coarse debris removal to fine particle filtration'
  ];

  const applications = [
    {
      title: 'Packing-Line Wash Water',
      description: 'Protect spray nozzles and pumps in fruit, vegetable, and food processing wash systems from debris and scale.'
    },
    {
      title: 'Spray-Bar Protection',
      description: 'Prevent nozzle clogging in automated wash, rinse, and cooling systems with inline filtration.'
    },
    {
      title: 'Coolant Filtration',
      description: 'Remove particulates from machine tool coolant and heat-exchanger water to extend equipment life.'
    },
    {
      title: 'Rinse-Water Recovery',
      description: 'Polish rinse water for reuse in closed-loop systems with automated self-cleaning filters.'
    },
    {
      title: 'Industrial Wastewater Polishing',
      description: 'Pre-treat process wastewater before discharge or further treatment to meet regulatory requirements.'
    }
  ];

  return (
    <>
      <SEOHead
        title="Industrial & Process Water Filtration | CTC Waterworks"
        description="CTC filtration systems serve food-and-beverage, packing, manufacturing, and washdown operations requiring reliable stainless-steel solids separation."
        canonical="https://www.ctcwaterworks.com/industries/industrial"
      />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-600 to-orange-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                    <Factory className="text-white" size={32} />
                  </div>
                  <h1 className="text-5xl font-bold">
                    Industrial & Process Water
                  </h1>
                </div>
                <p className="text-2xl text-orange-100 mb-6 font-light leading-relaxed">
                  CTC filtration systems serve food-and-beverage, packing, manufacturing, and washdown operations requiring reliable solids separation.
                </p>
                <p className="text-lg text-orange-100 leading-relaxed">
                  Stainless construction for long life and corrosion resistance. Continuous operation with automated cleaning. Compact footprint for skid or wall-mount integration.
                </p>
              </motion.div>

              <div className="bg-white rounded-lg shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Get Expert Help</h3>
                <Link
                  to="/contact?topic=industrial-filtration"
                  className="block w-full bg-orange-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors text-center shadow-lg mb-4"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>
                <p className="text-sm text-gray-600 text-center">
                  Our team will help you design the right filtration system for your process water application
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Why CTC Filtration for Industrial Applications?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Built for the demanding environments of food processing, manufacturing, and industrial washdown
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-orange-50 border border-orange-200 rounded-lg p-6">
                  <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                  <span className="text-gray-700 leading-relaxed">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Applications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Common Applications
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              CTC filtration systems protect industrial process equipment
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {applications.map((app, index) => (
                <div key={index} className="bg-white border-2 border-orange-200 rounded-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Systems Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
              Recommended Systems
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose from inline filters or water box assemblies based on your flow and space requirements
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {systems.map((system, index) => (
                <div key={index} className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-lg p-6 hover:shadow-xl transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{system.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{system.description}</p>
                  <Link
                    to={system.link}
                    className="flex items-center justify-between bg-white border-2 border-orange-600 text-orange-600 px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 hover:text-white transition-colors"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Highlight */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white border-2 border-orange-300 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Food-Grade Stainless Construction
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                All CTC inline filters and water boxes are constructed from 304 or 316 stainless steel, making them
                suitable for food-and-beverage applications, pharmaceutical processing, and other environments requiring
                sanitary design. Our systems resist corrosion from acids, detergents, and sanitizers commonly used in
                industrial washdown operations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">304/316 Stainless</p>
                  <p className="text-sm text-gray-600">Food-grade materials</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Corrosion Resistant</p>
                  <p className="text-sm text-gray-600">Acids & detergents</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="font-semibold text-gray-900">Sanitary Design</p>
                  <p className="text-sm text-gray-600">Easy to clean</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Process Equipment?</h2>
            <p className="text-xl text-orange-100 mb-8">
              Our engineering team will help you select the right filtration system for your flow rate, particle size, and space constraints.
            </p>
            <Link
              to="/contact?topic=industrial-filtration"
              className="inline-block bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Talk to a Filtration Expert
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default IndustrialIndustryPage;
